import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, KeyRound, Zap, CreditCard, MonitorPlay, Layers, Copy, Check,
  MessageSquare, Image as ImageIcon, Clapperboard, Terminal, ShieldCheck, Gauge, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { LOGO_URL } from "@/lib/brand";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { trackVisit } from "@/lib/track";

const BASE_V1 = `${API}/v1`;

const Logo = () => (
  <Link to="/" className="flex items-center gap-2.5" data-testid="landing-header-logo">
    <img src={LOGO_URL} alt="CRÉRÉA" className="h-10 w-10 rounded-lg object-contain ring-1 ring-gold-500/40" />
    <span className="font-display text-lg font-bold tracking-tight">CRÉRÉA</span>
  </Link>
);

const Eyebrow = ({ children }) => (
  <span className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-gold-400/90">{children}</span>
);

const TYPE_LABEL = { text: "Texte / LLM", image: "Image", video: "Vidéo" };
const TYPE_ICON = { text: MessageSquare, image: ImageIcon, video: Clapperboard };

const snippet = (lang) => {
  if (lang === "python")
    return `from openai import OpenAI

client = OpenAI(
    api_key="crea_votre_cle",
    base_url="${BASE_V1}",   # ← la seule ligne à changer
)

resp = client.chat.completions.create(
    model="crerea-pro",
    messages=[{"role": "user", "content": "Bonjour CRÉRÉA !"}],
)
print(resp.choices[0].message.content)`;
  if (lang === "node")
    return `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: "crea_votre_cle",
  baseURL: "${BASE_V1}",   // ← la seule ligne à changer
});

const resp = await client.chat.completions.create({
  model: "crerea-pro",
  messages: [{ role: "user", content: "Bonjour CRÉRÉA !" }],
});
console.log(resp.choices[0].message.content);`;
  return `curl ${BASE_V1}/chat/completions \\
  -H "Authorization: Bearer crea_votre_cle" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "crerea-pro",
    "messages": [{"role": "user", "content": "Bonjour CRÉRÉA !"}]
  }'`;
};

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [models, setModels] = useState([]);
  const [packs, setPacks] = useState([]);
  const [lang, setLang] = useState("python");
  const [copied, setCopied] = useState(false);
  const [filter, setFilter] = useState("all");
  const [loadingPack, setLoadingPack] = useState(null);

  useEffect(() => {
    trackVisit("/");
    api.get("/models").then((r) => setModels(r.data)).catch(() => {});
    api.get("/credits/packs").then((r) => setPacks(r.data)).catch(() => {});
  }, []);

  const go = () => navigate(user ? "/app" : "/login");
  const copy = (t) => { navigator.clipboard.writeText(t); setCopied(true); toast.success("Copié"); setTimeout(() => setCopied(false), 1500); };

  const buyPack = async (id) => {
    if (!user) { navigate("/login"); return; }
    setLoadingPack(id);
    try {
      const { data } = await api.post("/credits/checkout", { pack: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Paiement impossible. Réessayez."); setLoadingPack(null); }
  };

  const shown = filter === "all" ? models : models.filter((m) => m.type === filter);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/70 glass">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-8 lg:px-16">
          <Logo />
          <nav className="hidden items-center gap-8 lg:flex">
            {[["#modeles", "Modèles"], ["#pourquoi", "Pourquoi CRÉRÉA"], ["#studio", "Studio"], ["#tarifs", "Tarifs"], ["#docs", "Docs"]].map(([h, l]) => (
              <a key={h} href={h} className="text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid={`nav-link-${l}`}>{l}</a>
            ))}
            <Link to="/status" className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="nav-link-status">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />Statut
            </Link>
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            {user ? (
              <Button onClick={() => navigate("/app")} data-testid="nav-dashboard-btn" className="rounded-full bg-gold-500 text-black hover:bg-gold-400">Tableau de bord</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn" className="hidden sm:inline-flex">Connexion</Button>
                <Button onClick={() => navigate("/login")} data-testid="get-api-key-button" className="rounded-full bg-gold-500 text-black hover:bg-gold-400">
                  <KeyRound className="mr-1.5 h-4 w-4" />Obtenir une clé API
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 hero-radial" />
        <div className="pointer-events-none absolute -top-40 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-gold-500/15 blur-[130px]" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-8 lg:grid-cols-12 lg:px-16 lg:py-28">
          <div className="lg:col-span-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-gold-500/30 bg-gold-500/10 px-4 py-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />
                <Eyebrow>Passerelle IA frontière · Accès universel</Eyebrow>
              </div>
              <h1 className="font-display text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
                Une clé.<br /><span className="gold-text">Tous les modèles.</span>
              </h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                Accédez aux modèles de <span className="text-foreground">texte, image et vidéo</span> les plus puissants du monde via une API unique,
                <span className="text-foreground"> compatible à 100 % avec le SDK OpenAI</span>. Changez une seule ligne — <code className="font-mono text-gold-400">base_url</code>. Payez à l'usage, en crédits.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button size="lg" onClick={go} data-testid="hero-cta-signup" className="group rounded-full bg-gold-500 text-black hover:bg-gold-400 gold-halo">
                  Créer un compte & tester — 60 crédits offerts
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => document.getElementById("modeles")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-browse-catalog" className="rounded-full">
                  Parcourir le catalogue
                </Button>
              </div>
              <div className="mt-10 flex flex-wrap gap-6 text-sm text-muted-foreground">
                <span className="flex items-center gap-2"><Check className="h-4 w-4 text-gold-400" /> Zéro migration</span>
                <span className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-gold-400" /> Paiement aux crédits</span>
                <span className="flex items-center gap-2"><Gauge className="h-4 w-4 text-gold-400" /> Streaming natif</span>
              </div>
            </motion.div>
          </div>

          {/* Code snippet switcher */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="lg:col-span-6">
            <div className="overflow-hidden rounded-2xl border border-gold-500/25 bg-[#070B19] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)]">
              <div className="flex items-center justify-between border-b border-border/60 bg-card/60 px-4 py-2.5">
                <div className="flex items-center gap-1.5">
                  {["python", "node", "curl"].map((l) => (
                    <button key={l} onClick={() => setLang(l)} data-testid={`code-snippet-lang-${l}`}
                      className={`rounded-md px-3 py-1 font-mono text-xs transition-colors ${lang === l ? "bg-gold-500/15 text-gold-400" : "text-muted-foreground hover:text-foreground"}`}>
                      {l === "node" ? "Node.js" : l === "curl" ? "cURL" : "Python"}
                    </button>
                  ))}
                </div>
                <button onClick={() => copy(snippet(lang))} data-testid="code-snippet-copy" className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-gold-400">
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copié" : "Copier"}
                </button>
              </div>
              <pre className="overflow-x-auto p-5 font-mono text-[13px] leading-6 text-slate-200"><code>{snippet(lang)}</code></pre>
            </div>
            <p className="mt-3 text-center font-mono text-xs text-muted-foreground">base_url = <span className="text-gold-400">{BASE_V1}</span></p>
          </motion.div>
        </div>
      </section>

      {/* Model catalogue */}
      <section id="modeles" className="border-t border-border py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Eyebrow>Catalogue en temps réel</Eyebrow>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Un seul endpoint. Des dizaines de modèles.</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">Basculez entre texte, image et vidéo. Payez exactement ce que vous consommez, au crédit.</p>

          <div className="mt-8 flex flex-wrap gap-2">
            {[["all", "Tous"], ["text", "Texte / LLM"], ["image", "Image"], ["video", "Vidéo"]].map(([id, l]) => (
              <button key={id} onClick={() => setFilter(id)} data-testid={`model-filter-${id}`}
                className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${filter === id ? "border-gold-500 bg-gold-500/15 text-gold-400" : "border-border text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((m, i) => {
              const Icon = TYPE_ICON[m.type] || MessageSquare;
              return (
                <motion.div key={m.id} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 6) * 0.05 }}
                  className="group relative flex flex-col rounded-2xl border border-border bg-card p-5 transition-colors hover:border-gold-500/50" data-testid={`model-card-${m.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="grid h-9 w-9 place-items-center rounded-lg bg-gold-500/15 text-gold-400"><Icon className="h-4 w-4" /></div>
                      <div>
                        <p className="font-display text-base font-semibold leading-tight">{m.label}</p>
                        <span className="font-mono text-[11px] text-muted-foreground">{m.id}</span>
                      </div>
                    </div>
                    <span className="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{m.provider}</span>
                  </div>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{m.desc}</p>
                  <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
                    <span className="flex items-center gap-3 font-mono text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Gauge className="h-3 w-3" />{m.latency}</span>
                      <span>{m.context}</span>
                    </span>
                    <span className="font-mono text-sm font-semibold text-gold-400">{m.cost} cr.</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
            <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs">{TYPE_LABEL[filter] || "Tous les types"}</span>
            <span>Tester n'importe quel modèle dans le <button onClick={go} className="text-gold-400 underline-offset-2 hover:underline" data-testid="catalog-open-studio">Studio</button>.</span>
          </div>
        </div>
      </section>

      {/* Why bento */}
      <section id="pourquoi" className="border-t border-border bg-secondary/10 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Eyebrow>Pourquoi CRÉRÉA</Eyebrow>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Conçu pour les développeurs exigeants.</h2>
          <div className="mt-10 grid gap-4 md:grid-cols-12">
            {[
              { icon: Layers, span: "md:col-span-8", title: "0 migration, 100 % compatible OpenAI", desc: "Changez simplement base_url dans votre code existant. Vos prompts, vos SDK et vos outils restent intacts." },
              { icon: CreditCard, span: "md:col-span-4", title: "Paiement aux crédits", desc: "Pas d'abonnement caché. Le solde se met à jour en direct, à chaque appel réussi." },
              { icon: MonitorPlay, span: "md:col-span-4", title: "Studio web intégré", desc: "Testez, comparez et prototypez vos prompts dans le navigateur avant de déployer." },
              { icon: ShieldCheck, span: "md:col-span-8", title: "Haute disponibilité & faible latence", desc: "Routage intelligent des requêtes et gestion propre des erreurs — aucun crédit débité en cas d'échec." },
            ].map((c, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className={`${c.span} rounded-2xl border border-border bg-card p-6 lg:p-8 transition-colors hover:border-gold-500/40`} data-testid={`why-card-${i}`}>
                <div className="mb-4 grid h-11 w-11 place-items-center rounded-xl bg-gold-500/15 text-gold-400"><c.icon className="h-5 w-5" /></div>
                <h3 className="font-display text-xl font-semibold">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Studio teaser */}
      <section id="studio" className="border-t border-border py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-8 lg:grid-cols-2 lg:px-16">
          <div>
            <Eyebrow>Le Studio</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Un bac à sable pour tout expérimenter.</h2>
            <p className="mt-4 text-muted-foreground">Sélectionnez un modèle, écrivez votre prompt, et voyez la réponse arriver en streaming — texte, image ou vidéo — directement dans le navigateur.</p>
            <ul className="mt-6 space-y-3">
              {["Sélecteur dynamique de modèle", "Sortie texte en streaming haute vitesse", "Génération d'images & de vidéos avec aperçu"].map((f, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm"><Check className="h-4 w-4 shrink-0 text-gold-400" /><span className="text-muted-foreground">{f}</span></li>
              ))}
            </ul>
            <Button onClick={go} data-testid="studio-teaser-cta" className="mt-8 rounded-full bg-gold-500 text-black hover:bg-gold-400"><Sparkles className="mr-1.5 h-4 w-4" />Ouvrir le Studio</Button>
          </div>
          <div className="rounded-2xl border border-gold-500/25 bg-[#070B19] p-5 gold-halo">
            <div className="mb-3 flex items-center gap-2 border-b border-border pb-3">
              <div className="flex items-center gap-2 rounded-full bg-gold-500/15 px-3 py-1 font-mono text-xs text-gold-400"><MessageSquare className="h-3.5 w-3.5" />crerea-pro</div>
              <span className="ml-auto flex items-center gap-1.5 font-mono text-xs text-emerald-400"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" />streaming</span>
            </div>
            <p className="font-mono text-xs text-muted-foreground">&gt; Rédige un slogan pour une marque de café d'exception</p>
            <p className="mt-3 text-sm leading-relaxed text-slate-200">« Chaque tasse, une frontière franchie. » <span className="ml-0.5 inline-block h-4 w-1.5 translate-y-0.5 animate-pulse bg-gold-400" /></p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="tarifs" className="border-t border-border bg-secondary/10 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <div className="text-center">
            <Eyebrow>Packs de crédits</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Transparent. Sans abonnement. Sans expiration.</h2>
          </div>
          <div className="mx-auto mt-12 grid max-w-5xl gap-6 lg:grid-cols-3">
            {packs.map((p, i) => (
              <div key={p.id} data-testid={`credit-pack-${p.id}`} className={`relative flex flex-col rounded-2xl border p-7 ${i === 1 ? "border-gold-500 bg-card glow-emerald" : "border-border bg-card"}`}>
                {i === 1 && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gold-500 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-black">Le plus populaire</span>}
                <h3 className="font-display text-xl font-bold">{p.label}</h3>
                <p className="mt-4 flex items-baseline gap-1"><span className="font-display text-4xl font-bold text-gold-400">{(p.amount / 100).toFixed(0)}€</span></p>
                <p className="mt-1 text-sm text-muted-foreground">{p.credits.toLocaleString("fr-FR")} crédits · {(p.amount / p.credits / 100).toFixed(3)} €/crédit</p>
                <ul className="mt-5 flex-1 space-y-2.5 text-sm">
                  {["Accès à tous les modèles", "Clés API illimitées", "Studio & streaming inclus", i === 2 ? "Analytique avancée" : "Docs & support"].map((f, j) => (
                    <li key={j} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-400" /><span className="text-muted-foreground">{f}</span></li>
                  ))}
                </ul>
                <Button onClick={() => buyPack(p.id)} disabled={loadingPack === p.id} data-testid={`credit-pack-${p.id}-buy-btn`}
                  className={`mt-6 w-full rounded-full ${i === 1 ? "bg-gold-500 text-black hover:bg-gold-400" : ""}`} variant={i === 1 ? "default" : "outline"}>
                  {loadingPack === p.id ? "Redirection…" : user ? "Recharger" : "Commencer"}
                </Button>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-xs text-muted-foreground">Paiement sécurisé Stripe · carte test 4242 4242 4242 4242 en mode démo</p>
        </div>
      </section>

      {/* Quickstart / docs */}
      <section id="docs" className="border-t border-border py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <div className="text-center">
            <Eyebrow>Déploiement en 30 secondes</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Trois étapes, et c'est en production.</h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {[
              { n: "01", icon: KeyRound, t: "Générez votre clé", d: "Créez un compte et générez une clé crea_… dans l'onglet Développeurs." },
              { n: "02", icon: Terminal, t: "Changez la base_url", d: "Pointez votre SDK OpenAI vers l'endpoint CRÉRÉA. Rien d'autre à modifier." },
              { n: "03", icon: Zap, t: "Lancez vos requêtes", d: "Appelez n'importe quel modèle — texte, image, vidéo — et payez au crédit." },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-border bg-card p-6" data-testid={`quickstart-step-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-gold-500/15 text-gold-400"><s.icon className="h-5 w-5" /></div>
                  <span className="font-display text-3xl font-bold text-border">{s.n}</span>
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
              </motion.div>
            ))}
          </div>

          {/* Final CTA */}
          <div className="mt-16 overflow-hidden rounded-3xl border border-gold-500/30 bg-card p-10 text-center gold-halo shimmer relative">
            <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Prêt à unifier vos modèles d'IA ?</h2>
            <p className="mx-auto mt-3 max-w-xl text-muted-foreground">Rejoignez la plateforme nouvelle génération. Crédits offerts à l'inscription, aucune carte requise pour tester.</p>
            <Button size="lg" onClick={go} data-testid="final-cta-btn" className="mt-7 rounded-full bg-gold-500 text-black hover:bg-gold-400">
              Obtenir ma clé API <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 sm:flex-row sm:px-8 lg:px-16">
          <Logo />
          <div className="flex items-center gap-6">
            <Link to="/status" className="text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="footer-status">Statut & modèles</Link>
            <p className="text-sm text-muted-foreground">© 2026 CRÉRÉA — Une clé, tous les modèles.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
